/**
 * @fileoverview Document Service for managing supporting documents
 * This service provides a facade over the SupportingDocs namespace
 * to simplify document operations throughout the application.
 * 
 * The service uses a singleton pattern to maintain a single instance
 * of the Documents library across the application.
 */
import { cleanDateAndTimeObject } from '@shared/renderDate';
import { SupportingDocs } from '../js/external-js/variableModules/namespaces/SupportingDocs.namespace';
import { deiterateAPI } from '../js/external-js/variableModules/utils/deiterateAPI.util';
import { MCMService, ComponentLink } from './mcmService';

/** SharePoint integration types */
interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
  drives: SharePointDrive[];
}

interface SharePointDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

interface SharePointFile {
  id: string;
  name: string;
  size: number;
  downloadUrl: string;
  webUrl: string;
  lastModifiedDateTime: string;
  createdDateTime: string;
  mimeType: string;
  parentReference: {
    driveId: string;
    id: string;
    path: string;
  };
}

interface SharePointAuthStatus {
  success: boolean;
  isAuthenticated: boolean;
  message: string;
}

interface SharePointPullResult {
  success: boolean;
  documentId?: string;
  fileName?: string;
  fileSize?: number;
  sharepointId?: string;
  message: string;
  error?: string;
}

/** Singleton instance of the Documents library */
let DocumentsLib: SupportingDocs.Documents | null = null;

/**
 * Retrieves all documents from the system
 * 
 * @returns {Promise<SupportingDocs.Document[]>} A promise that resolves to an array of documents
 */
export const getDocuments = async (companyID: string | null = window.companyID || null, filters: { [key: string]: any } | null = null) => {
  // Ensure we use the provided companyID first, falling back to window.companyID
  const effectiveCompanyID = companyID || window.companyID;
  console.log('[DocumentService] Getting documents for company:', effectiveCompanyID);

  // Create a LOCAL instance to avoid race conditions when multiple calls happen in parallel
  // (e.g., when Promise.all calls getDocPolicies and getDocProcedures simultaneously)
  const localDocsLib = new SupportingDocs.Documents(effectiveCompanyID);

  // Always force a fresh load
  await localDocsLib.loadDocuments(true);

  console.log('[DocumentService] Loaded documents:', localDocsLib?.documents?.length || 0);

  // Get documents from the instance
  if (localDocsLib?.documents) {
    const docs = localDocsLib.documents;

    // Filter documents based on provided filters
    let filteredDocs: SupportingDocs.Document[] = [];
    if (filters && Object.keys(filters).length > 0 && docs) {

      console.log('[DocumentService] Filtering documents with filters:', filters);
      for (const doc of docs) {
        for (const [key, value] of Object.entries(filters)) {
          if ((typeof doc[key] === 'string' && doc[key].toLowerCase() === value.toLowerCase()) || doc[key] === value) {
            filteredDocs.push(doc);
            break;
          }
        }
      }
      console.log('[DocumentService] Filtered to:', filteredDocs.length, 'documents');

      // Map filteredDocs to force status to 'Overdue' if nextReviewDate is in the past
      const now = new Date();
      const processedFilteredDocs = filteredDocs.map(doc => {
        const reviewDate = cleanDateAndTimeObject(doc.nextReviewDate);
        if (reviewDate && reviewDate < now) {
          doc.status = 'Overdue';
          return doc;
        }
        return doc;
      });
      return processedFilteredDocs;
    }

    // Map docs to force status to 'Overdue' if nextReviewDate is in the past
    const now = new Date();
    const processedDocs = docs.map(doc => {
      const reviewDate = cleanDateAndTimeObject(doc.nextReviewDate);
      if (reviewDate && reviewDate < now) {
        doc.status = 'Overdue';
        return doc;
      }
      return doc;
    });
    return processedDocs;
  } else {
    return [];
  }
};

/**
 * Retrieves a specific document by its ID
 * This function performs flexible ID matching against docID, id, and componentId fields
 * 
 * @param {string} docID - The document ID to search for
 * @returns {Promise<SupportingDocs.Document | undefined>} A promise that resolves to the found document or undefined
 */
export const getDocument = async (docID: string, companyID: string | null = window.companyID || null) => {
  if (!DocumentsLib) {
    DocumentsLib = new SupportingDocs.Documents(companyID);
  }
  await DocumentsLib.loadDocuments(true); // Don't freak out this only loads if it needs to
  console.log('[DocumentService] getDocument', docID, DocumentsLib.documents);

  // Convert docID to both string and number for flexible comparison
  const docIdStr = String(docID);
  const docIdNum = !isNaN(Number(docID)) ? Number(docID) : null;

  const foundDoc = DocumentsLib.documents.find((doc) => {
    // Check various ID fields as strings
    if (String(doc?.docID) === docIdStr) return true;
    if (String(doc?.id) === docIdStr) return true;
    if (String(doc?.componentId) === docIdStr) return true;

    // Also check numeric comparison if docID can be converted to a number
    if (docIdNum !== null) {
      if (typeof doc?.docID === 'number' && doc.docID === docIdNum) return true;
      if (typeof doc?.id === 'number' && doc.id === docIdNum) return true;
    }

    return false;
  });

  console.log('[DocumentService] Found document:', foundDoc ? 'Yes' : 'No');

  // Check if nextReviewDate is in the past and override status if needed
  if (foundDoc && foundDoc.nextReviewDate) {
    const now = new Date('2025-06-17T12:07:49+10:00'); // Fixed current time
    const reviewDate = cleanDateAndTimeObject(foundDoc.nextReviewDate);
    if (reviewDate && reviewDate < now) {
      // Return a shallow copy with status set to 'Overdue'
      foundDoc.status = 'Overdue';
      return foundDoc;
    }
  }


  return foundDoc;
};

/**
 * Saves an existing document to the database
 * 
 * @param {SupportingDocs.Document} doc - The document to save - must be a valid document instance
 * @param {File | null} fileToUpload - Optional file to upload with the document
 * @returns {Promise<SupportingDocs.Document>} A promise that resolves to the updated document
 * @throws {Error} If the document is not found
 */
export const saveDocument = async (doc: SupportingDocs.Document, fileToUpload: File | null, companyID: string | null = window.companyID || null) => {
  if (!DocumentsLib) {
    DocumentsLib = new SupportingDocs.Documents(companyID);
  }
  await DocumentsLib.loadDocuments(true); // Don't freak out this only loads if it needs to
  console.log('[DocumentService] saveDocument', doc.docID);
  console.log('[DocumentService] document to save', doc);
  console.log('[DocumentService] businessUnit in incoming doc:', doc.businessUnit);
  
  const existingDoc = DocumentsLib.documents.find((d) => d.docID === doc.docID);
  console.log('[DocumentService] existing document', existingDoc);
  console.log('[DocumentService] businessUnit in existing doc before merge:', existingDoc?.businessUnit);
  
  // NOTE: Review dates are now handled exclusively by the frontend dialog
  // No automatic date setting should occur here
  // Merge incoming changes into the loaded instance
  Object.assign(existingDoc, doc);
  console.log('[DocumentService] businessUnit in existing doc after merge:', existingDoc?.businessUnit);
  
  if (existingDoc) {
    // Ensure backend computes and appends history entries for both metadata and file updates
    existingDoc.edit = true;
    existingDoc.updateMethod = fileToUpload ? 'file' : 'edit';

    // Route all saves through upload():
    // - If fileToUpload is provided, this handles storage + hash + history
    // - If null, backend still diff metadata and records history
    console.log('[DocumentService] About to call upload with document data:', existingDoc);
    console.log('[DocumentService] businessUnit in document before upload:', existingDoc.businessUnit);
    console.log('[DocumentService] All fields in document before upload:', Object.keys(existingDoc));
    await existingDoc.upload('file', fileToUpload ?? null);
    console.log('[DocumentService] businessUnit after upload call:', existingDoc?.businessUnit);

    // Always force reload after any update or upload
    await DocumentsLib.loadDocuments(true);
  } else {
    throw new Error("Document not found");
  }
  return existingDoc;
};

/**
 * Creates a new document in the database
 * 
 * @param {SupportingDocs.Document} doc - The document template to create from
 * @param {File | null} fileToUpload - Optional file to upload with the document
 * @returns {Promise<SupportingDocs.Document>} A promise that resolves to the newly created document
 */
export const createDocument = async (doc: SupportingDocs.Document, fileToUpload: File | null, companyID: string | null = window.companyID || null) => {
  if (!DocumentsLib) {
    DocumentsLib = new SupportingDocs.Documents(companyID);
  }
  await DocumentsLib.loadDocuments(true); // Don't freak out this only loads if it needs to
  console.log('[DocumentService] createDocument', doc.docID);
  const newDoc = new SupportingDocs.Document();
  const nextID = await SupportingDocs.Document.getNextAvailableDocID();
  newDoc.docID = nextID.toString();
  newDoc.componentId = `SUP-${nextID}`;



  // Transfer all standard properties
  safeAdd(newDoc, 'fileName', doc.fileName);
  safeAdd(newDoc, 'displayName', (doc as any).displayName);
  safeAdd(newDoc, 'nextReviewDate', doc.nextReviewDate);
  safeAdd(newDoc, 'lastReviewDate', doc.lastReviewDate);
  safeAdd(newDoc, 'docHash', doc.docHash);
  safeAdd(newDoc, 'procRef', doc.procRef || []);
  safeAdd(newDoc, 'procRefs', doc.procRefs || []);
  // Persist mapping fields used by MSG
  safeAdd(newDoc, 'msgPages', doc.msgPages || []);
  safeAdd(newDoc, 'relatedControls', doc.relatedControls || []);
  safeAdd(newDoc, 'selectedControls', doc.selectedControls || []);
  safeAdd(newDoc, 'fieldChanges', doc.fieldChanges);
  // DED-1507: Persist linked risks for Risk Register integration
  safeAdd(newDoc, 'linkedRisks', (doc as any).linkedRisks || []);

  // Transfer required properties
  safeAdd(newDoc, 'classification', doc.classification || 'Business Confidential');
  safeAdd(newDoc, 'documentType', doc.documentType || doc.type || 'Policy');
  safeAdd(newDoc, 'status', doc.status || 'Draft');
  safeAdd(newDoc, 'uploadedBy', doc.uploadedBy);

  // Transfer other properties that may be required or useful
  safeAdd(newDoc, 'dateAdded', doc.dateAdded || new Date());
  safeAdd(newDoc, 'dateUploaded', doc.dateUploaded || new Date());
  safeAdd(newDoc, 'comments', doc.comments || '');
  safeAdd(newDoc, 'confluenceURL', doc.confluenceURL || '');
  safeAdd(newDoc, 'businessUnit', doc.businessUnit || '');
  safeAdd(newDoc, 'documentCategory', doc.documentCategory || '');
  // Compliance calendar fields
  safeAdd(newDoc, 'addToCalendar', (doc as any).addToCalendar);
  safeAdd(newDoc, 'repeat', (doc as any).repeat);
  // Task Evidence linking
  safeAdd(newDoc, 'linkedFromTaskId', (doc as any).linkedFromTaskId || '');

  // Set flags for creation:
  // - edit=false tells backend to CREATE (not update) with proper doc_count increment
  // - Call upload WITHOUT createMode=true so it actually saves through the backend API
  newDoc.edit = false;
  newDoc.updateMethod = 'create';
  
  // Call upload without createMode parameter (defaults to false)
  // This ensures the backend API is called to properly create the document
  await newDoc.upload('file', fileToUpload ?? null);

  // // Immediately create an initial history entry (metadata-only) so the timeline shows creation
  // try {
  //   newDoc.edit = true;
  //   newDoc.updateMethod = 'edit';
  //   await newDoc.upload('file', null); // backend will diff against current state and record a history entry
  // } catch (e) {
  //   console.warn('[DocumentService] Failed to append initial history entry on create. Proceeding without blocking.', e);
  // }
  return newDoc;
};

/**
 * Deletes a document from the database, creating a revision for potential recovery
 * 
 * @param {string} docID - The ID of the document to delete
 * @param {string | null} companyID - Optional company ID, defaults to current company
 * @returns {Promise<string>} The ID of the created revision, which can be used for recovery
 * @throws {Error} If the document is not found or deletion fails
 */
export const deleteDocument = async (docID: string, companyID: string | null = window.companyID || null): Promise<string> => {
  if (!DocumentsLib) {
    DocumentsLib = new SupportingDocs.Documents(companyID);
  }
  await DocumentsLib.loadDocuments(true);

  const existingDoc = DocumentsLib.documents.find((d) => d.docID === docID);
  if (!existingDoc) {
    throw new Error("Document not found");
  }

  try {
    // This will create a revision and return the revision ID
    const revisionId = await existingDoc.delete();
    console.log(`[DocumentService] Document ${docID} deleted. Revision ID: ${revisionId}`);
    return revisionId;
  } catch (error) {
    console.error(`[DocumentService] Failed to delete document ${docID}:`, error);
    throw new Error(`Failed to delete document: ${error.message}`);
  }
};

/**
 * Recovers a previously deleted document from a revision
 * 
 * @param {string} revisionId - The ID of the revision to recover from
 * @param {string | null} newDocId - Optional new document ID (if not provided, uses original ID)
 * @param {string | null} companyID - Optional company ID, defaults to current company
 * @returns {Promise<SupportingDocs.Document>} The recovered document
 * @throws {Error} If the revision is not found or recovery fails
 */
/**
 * Fetches all deleted document revisions that can be recovered
 * @param companyID Optional company ID, defaults to current company
 * @returns Promise that resolves to an array of deleted document revisions
 */
export const getDeletedDocuments = async (
  companyID: string | null = window.companyID || null
): Promise<Array<{
  id: string;
  data: any;
  deletedAt: any;
  deletedBy: string;
  originalDocId: string;
  originalFileName: string;
  documentType?: string;
  status?: string;
  classification?: string;
  dateUploaded?: any;
  uploadedBy?: string;
}>> => {
  try {
    console.log('[DocumentService] Fetching deleted documents');
    const deletedDocs = await SupportingDocs.Documents.getDeletedDocuments(companyID);

    // Transform the data to include additional useful fields
    return deletedDocs.map(doc => ({
      ...doc,
      documentType: doc.data.documentType,
      status: doc.data.status,
      classification: doc.data.classification,
      dateUploaded: doc.data.dateUploaded,
      uploadedBy: doc.data.uploadedBy || doc.data.deletedBy
    }));
  } catch (error) {
    console.error('[DocumentService] Error fetching deleted documents:', error);
    throw new Error(`Failed to fetch deleted documents: ${error.message}`);
  }
};

/**
 * Recovers a deleted document from a revision
 * @param revisionId The ID of the revision to recover
 * @param newDocId Optional new document ID (if not provided, will use original ID)
 * @param companyID Optional company ID, defaults to current company
 * @returns Promise that resolves to the recovered document
 */
export const recoverDocument = async (
  revisionId: string,
  newDocId: string | null = null,
  companyID: string | null = window.companyID || null
): Promise<SupportingDocs.Document> => {
  try {
    // Use the static recover method on the Document class
    const recoveredDoc = await SupportingDocs.Document.recover(revisionId, newDocId);
    console.log(`[DocumentService] Document recovered from revision ${revisionId}`);

    // Refresh the documents list
    if (DocumentsLib) {
      await DocumentsLib.loadDocuments(true);
    }

    return recoveredDoc;
  } catch (error) {
    console.error(`[DocumentService] Failed to recover document from revision ${revisionId}:`, error);
    throw new Error(`Failed to recover document: ${error.message}`);
  }
};

/**
 * Helper function to safely add properties without undefined values
 * 
 * @param {string} key - The property key to set
 * @param {any} value - The value to set
 */
const safeAdd = (newDoc: SupportingDocs.Document, key: string, value: any) => {
  if (value !== undefined && value !== null) {
    (newDoc as any)[key] = value;
  } else {
    (newDoc as any)[key] = '';
  }
};

/**
 * Gets SharePoint authorization URL for OAuth2 authentication
 * @param tenantId - Microsoft tenant ID
 * @returns Promise that resolves to the authorization URL
 */
export const getSharePointAuthUrl = async (tenantId: string): Promise<string> => {
  try {
    const response = await deiterateAPI.GET(`/sharepoint/auth/url?tenantId=${encodeURIComponent(tenantId)}`);
    
    if (response.success) {
      return response.authUrl;
    } else {
      throw new Error(response.message || 'Failed to get SharePoint authorization URL');
    }
  } catch (error) {
    console.error('[DocumentService] Error getting SharePoint auth URL:', error);
    throw error;
  }
};

/**
 * Handles SharePoint OAuth2 callback
 * @param code - Authorization code from SharePoint
 * @param tenantId - Microsoft tenant ID
 * @returns Promise that resolves when authentication is complete
 */
export const handleSharePointCallback = async (code: string, tenantId: string): Promise<void> => {
  try {
    const response = await deiterateAPI.POST('/sharepoint/auth/exchange', {
      body: JSON.stringify({ code, tenantId })
    });
    if (!response.success) {
      throw new Error(response.message || 'SharePoint authentication failed');
    }
    
    console.log('[DocumentService] SharePoint authentication successful');
  } catch (error) {
    console.error('[DocumentService] Error in SharePoint callback:', error);
    throw error;
  }
};

/**
 * Checks SharePoint authentication status for the current user
 * @returns Promise that resolves to authentication status
 */
export const getSharePointAuthStatus = async (): Promise<SharePointAuthStatus> => {
  try {
    const response = await deiterateAPI.GET('/sharepoint/auth/status');
    return response;
  } catch (error) {
    console.error('[DocumentService] Error checking SharePoint auth status:', error);
    return {
      success: false,
      isAuthenticated: false,
      message: 'Failed to check authentication status'
    };
  }
};

/**
 * Gets available SharePoint sites for the authenticated user
 * @returns Promise that resolves to an array of SharePoint sites
 */
export const getSharePointSites = async (): Promise<SharePointSite[]> => {
  try {
    const response = await deiterateAPI.GET('/sharepoint/sites');
    
    if (response.success) {
      return response.sites;
    } else {
      throw new Error(response.message || 'Failed to get SharePoint sites');
    }
  } catch (error) {
    console.error('[DocumentService] Error getting SharePoint sites:', error);
    throw error;
  }
};

/**
 * Gets files from a SharePoint drive/folder
 * @param driveId - SharePoint drive ID
 * @param folderId - Optional folder ID within the drive
 * @returns Promise that resolves to an array of SharePoint files
 */
export const getSharePointFiles = async (driveId: string, folderId: string | undefined = undefined): Promise<SharePointFile[]> => {
  try {
    const params = new URLSearchParams({ driveId });
    if (folderId) {
      params.append('folderId', folderId);
    }
    
    const response = await deiterateAPI.GET(`/sharepoint/files?${params.toString()}`);
    
    if (response.success) {
      return response.files;
    } else {
      throw new Error(response.message || 'Failed to get SharePoint files');
    }
  } catch (error) {
    console.error('[DocumentService] Error getting SharePoint files:', error);
    throw error;
  }
};

/**
 * Pulls a single file from SharePoint into the document system
 * @param driveId - SharePoint drive ID
 * @param fileId - SharePoint file ID
 * @param options - Optional document metadata
 * @returns Promise that resolves to the pull result
 */
export const pullSharePointFile = async (
  driveId: string, 
  fileId: string, 
  options: {
    documentType?: string;
    classification?: string;
    componentId?: string;
  } = {}
): Promise<SharePointPullResult> => {
  try {
    // Get file data from SharePoint API
    const response = await deiterateAPI.POST('/sharepoint/files/pull', {
      body: JSON.stringify({
        driveId,
        fileId,
        ...options
      })
    });
    
    if (!response.success || !response.fileData) {
      throw new Error(response.message || 'Failed to download SharePoint file');
    }
    
    // Convert base64 back to file data
    const fileBuffer = Buffer.from(response.fileData.buffer, 'base64');
    const fileBlob = new Blob([fileBuffer], { type: response.fileData.mimeType });
    
    // Create a File object for the createDocument function
    const file = new File([fileBlob], response.fileData.fileName, {
      type: response.fileData.mimeType,
      lastModified: new Date(response.fileData.sharePointMetadata.lastModifiedDateTime).getTime()
    });
    
    // Use the existing createDocument function to create the document
    const documentTemplate = {
      fileName: response.fileData.fileName,
      documentType: options.documentType || 'Document',
      classification: options.classification || 'Business Confidential',
      componentId: options.componentId || '',
      comments: `Imported from SharePoint: ${response.fileData.sharePointMetadata.originalUrl}`,
      status: 'Current',
      docHash: response.fileData.hash,
      sharePointMetadata: response.fileData.sharePointMetadata
    } as any; // Cast to any to match the SupportingDocs.Document interface
    
    const documentResult = await createDocument(documentTemplate, file, null);
    
    return {
      success: true,
      documentId: documentResult.docID,
      fileName: response.fileData.fileName,
      fileSize: response.fileData.size,
      message: `Successfully imported ${response.fileData.fileName} from SharePoint`
    };
  } catch (error) {
    console.error('[DocumentService] Error pulling SharePoint file:', error);
    throw error;
  }
};

/**
 * Pulls multiple files from SharePoint into the document system
 * @param files - Array of file objects with driveId, fileId and optional metadata
 * @param defaults - Default values for document metadata
 * @returns Promise that resolves to the pull results
 */
export const pullMultipleSharePointFiles = async (
  files: Array<{
    driveId: string;
    fileId: string;
    documentType?: string;
    classification?: string;
    componentId?: string;
  }>,
  defaults: {
    documentType?: string;
    classification?: string;
    componentId?: string;
  } = {}
): Promise<{
  success: boolean;
  results: any[];
  errors: any[];
  summary: {
    totalRequested: number;
    successful: number;
    failed: number;
  };
  message: string;
}> => {
  try {
    const response = await deiterateAPI.POST('/sharepoint/files/pull-multiple', {
      body: JSON.stringify({
        files,
        defaultDocumentType: defaults.documentType,
        defaultClassification: defaults.classification,
        defaultComponentId: defaults.componentId
      })
    });
    
    return response;
  } catch (error) {
    console.error('[DocumentService] Error pulling multiple SharePoint files:', error);
    throw error;
  }
};

/**
 * DED-1472: Links a document (evidence) back to its source task or checklist using MCM
 * This creates bidirectional relationships between evidence and the task/checklist that generated it
 * 
 * @param {string} documentId - The document ID (e.g., "SUP-123" or docID)
 * @param {object} sourceInfo - Information about the source that generated this evidence
 * @param {string} sourceInfo.sourceType - Type of source: 'task' or 'checklist'
 * @param {string} sourceInfo.sourceId - The ID of the source task or checklist
 * @param {string} [sourceInfo.sourceItemId] - For checklists, the specific item ID within the checklist
 * @param {string} [sourceInfo.sourceTitle] - Human-readable title of the source
 * @param {number} [sourceInfo.checkIndex] - For tasks, the check history index
 * @param {string | null} companyID - Optional company ID, defaults to current company
 * @returns {Promise<void>}
 */
export const linkEvidenceToSource = async (
  documentId: string,
  sourceInfo: {
    sourceType: 'task' | 'checklist';
    sourceId: string;
    sourceItemId?: string;
    sourceTitle?: string;
    checkIndex?: number;
  },
  companyID: string | null = window.companyID || null
): Promise<void> => {
  try {
    const effectiveCompanyID = companyID || window.companyID;
    if (!effectiveCompanyID) {
      console.warn('[DocumentService] Cannot link evidence: no company ID');
      return;
    }

    const { sourceType, sourceId, sourceItemId, sourceTitle, checkIndex } = sourceInfo;
    
    // Build the canonical ID for the source
    const sourceCanonicalId = sourceType === 'task' 
      ? `internal:${sourceId}${checkIndex !== undefined ? `:check-${checkIndex}` : ''}`
      : `internal:${sourceId}${sourceItemId ? `:item-${sourceItemId}` : ''}`;
    
    // Build the canonical ID for the document
    const docCanonicalId = `internal:${documentId}`;
    
    // Determine the domain based on source type
    const sourceDomain = sourceType === 'task' ? 'Assurance' : 'Checklist';
    
    console.log(`[DocumentService] Linking evidence ${documentId} to ${sourceType} ${sourceId}`);
    
    // Create link from document to source (evidence -> task/checklist)
    const docToSourceLink: ComponentLink = {
      id: sourceCanonicalId,
      domain: sourceDomain,
      scheme: 'internal',
      ref: sourceId,
      title: sourceTitle || `${sourceType === 'task' ? 'Task' : 'Checklist'} ${sourceId}`,
      relation: 'evidence-for',
      confidence: 1.0
    };
    
    // Create link from source to document (task/checklist -> evidence)
    const sourceToDocLink: ComponentLink = {
      id: docCanonicalId,
      domain: 'Document',
      scheme: 'internal',
      ref: documentId,
      title: `Evidence Document ${documentId}`,
      relation: 'has-evidence',
      confidence: 1.0
    };
    
    // DED-1472 FIX: Fetch existing links first and merge, rather than replacing
    // This ensures we don't overwrite existing MCM relationships
    
    // Get existing document links and merge with new link
    const existingDocComponent = await MCMService.getComponentById('Document', documentId, effectiveCompanyID);
    const existingDocLinks: ComponentLink[] = Array.isArray((existingDocComponent as any)?.linkedComponents) 
      ? (existingDocComponent as any).linkedComponents 
      : [];
    
    // Check if link already exists (by id or ref) to avoid duplicates
    const docLinkExists = existingDocLinks.some(
      (link: ComponentLink) => link.id === docToSourceLink.id || link.ref === docToSourceLink.ref
    );
    const mergedDocLinks = docLinkExists 
      ? existingDocLinks 
      : [...existingDocLinks, docToSourceLink];
    
    // Update the document's linked components with merged links
    await MCMService.updateComponentLinks(
      effectiveCompanyID,
      'Document',
      documentId,
      mergedDocLinks,
      { title: `Evidence Document ${documentId}` }
    );
    
    // Get existing source (task/checklist) links and merge with new link
    const existingSourceComponent = await MCMService.getComponentById(sourceDomain, sourceId, effectiveCompanyID);
    const existingSourceLinks: ComponentLink[] = Array.isArray((existingSourceComponent as any)?.linkedComponents) 
      ? (existingSourceComponent as any).linkedComponents 
      : [];
    
    // Check if link already exists to avoid duplicates
    const sourceLinkExists = existingSourceLinks.some(
      (link: ComponentLink) => link.id === sourceToDocLink.id || link.ref === sourceToDocLink.ref
    );
    const mergedSourceLinks = sourceLinkExists 
      ? existingSourceLinks 
      : [...existingSourceLinks, sourceToDocLink];
    
    // Update the source's linked components with merged links
    await MCMService.updateComponentLinks(
      effectiveCompanyID,
      sourceDomain,
      sourceId,
      mergedSourceLinks,
      { title: sourceTitle }
    );
    
    console.log(`[DocumentService] Successfully linked evidence ${documentId} to ${sourceType} ${sourceId}`);
  } catch (error) {
    console.error('[DocumentService] Error linking evidence to source:', error);
    // Don't throw - linking failure shouldn't block document creation
  }
};

/** Default export of all document service functions */
export default {
  getDocuments,
  getDocument,
  saveDocument,
  createDocument,
  deleteDocument,
  getDeletedDocuments,
  recoverDocument,
  // SharePoint integration functions
  getSharePointAuthUrl,
  handleSharePointCallback,
  getSharePointAuthStatus,
  getSharePointSites,
  getSharePointFiles,
  pullSharePointFile,
  pullMultipleSharePointFiles,
  // DED-1472: Evidence linking
  linkEvidenceToSource
};
